/**
 * analytics.ts — Consent-aware GTM / GA4 event tracking.
 *
 * GTM container (GTM-WFZZ58HF) is loaded in index.html.
 * We push structured events to window.dataLayer; GTM forwards them to GA4.
 * Events are dropped unless GA4 is accepted, never replayed after acceptance.
 *
 * All GA4 event names use snake_case convention (Google standard).
 * E-commerce events follow GA4 Enhanced Ecommerce item schema.
 */

import { hasServiceConsent } from './consent';
import { canFireMeta, googleCommand, googleConsentState } from './tracking-runtime';

/* ─── Core push ──────────────────────────────────────────────────────── */

function push(event: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !hasServiceConsent('ga4')) return;
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ event, ...params });
}

/* ─── Meta Pixel helpers ────────────────────────────────────────────── */

/** Whether the Meta Pixel (fbq) is loaded and marketing consent is given. */
function canFireFbq(): boolean {
  return canFireMeta();
}

/* ─── Consent mode ──────────────────────────────────────────────────── */

/**
 * Notify GTM of a consent update.
 * Call this after the user submits their cookie choices.
 * GTM's consent mode v2 uses this to gate tag firing.
 */
export function pushConsentUpdate(
  _statistics: boolean,
  _marketing: boolean,
): void {
  const state = googleConsentState();
  googleCommand('consent', 'update', state);
  const w = window as any;
  w.dataLayer.push({ event: 'consent_update', ...state });
}

/* ─── Engagement events ─────────────────────────────────────────────── */

/** A page section entered the viewport (threshold ≥ 30 %). */
export function trackSectionView(sectionId: string): void {
  push('section_view', { section_id: sectionId });
}

/** User spent ≥ 5 s in a section. */
export function trackSectionEngaged(sectionId: string, durationMs: number): void {
  push('section_engaged', { section_id: sectionId, duration_ms: durationMs });
}

/** User scrolled past a depth milestone. */
export function trackScrollDepth(depth: number, pagePath: string): void {
  push('scroll_depth', { scroll_depth: depth, page_path: pagePath });
}

/** Product detail sheet opened. */
export function trackProductDetailView(productId: string): void {
  push('product_detail_view', { product_id: productId });
}

/** FAQ accordion item toggled. */
export function trackFaqToggle(index: number, action: 'open' | 'close'): void {
  push('faq_toggle', { faq_index: index, action });
}

/** InfoTooltip (popover) opened. */
export function trackTooltipOpen(label: string): void {
  push('tooltip_open', { tooltip_label: label });
}

/* ─── E-commerce events (GA4 Enhanced Ecommerce) ────────────────────── */
/* The `ecommerce` wrapper is required by GA4's built-in e-commerce data  */
/* layer format. GTM's "Send Ecommerce data" setting reads from this.      */

export function trackAddToCart(
  productId: string,
  productName: string,
  price: number,
  quantity: number,
): void {
  push('add_to_cart', {
    ecommerce: {
      currency: 'EUR',
      value: price * quantity,
      items: [{ item_id: productId, item_name: productName, price, quantity }],
    },
  });

  if (canFireFbq()) {
    (window as any).fbq('track', 'AddToCart', {
      value: price * quantity,
      currency: 'EUR',
      content_ids: [productId],
      content_name: productName,
      content_type: 'product',
      contents: [{ id: productId, quantity, item_price: price }],
    });
  }
}

export function trackRemoveFromCart(productId: string, productName: string, price: number): void {
  push('remove_from_cart', {
    ecommerce: {
      currency: 'EUR',
      value: price,
      items: [{ item_id: productId, item_name: productName, price, quantity: 1 }],
    },
  });
}

export function trackCartOpen(itemCount: number): void {
  push('cart_open', { item_count: itemCount, currency: 'EUR' });
}

export function trackPromoCodeApplied(code: string): void {
  push('promo_code_applied', { promo_code: code });
}

export function trackPromoCodeFailed(code: string, reason: string): void {
  push('promo_code_failed', { promo_code: code, reason });
}

export function trackBeginCheckout(
  items: Array<{ id: string; name: string; price: number; quantity: number }>,
  total: number,
): void {
  push('begin_checkout', {
    ecommerce: {
      currency: 'EUR',
      value: total,
      items: items.map((i) => ({
        item_id: i.id,
        item_name: i.name,
        price: i.price,
        quantity: i.quantity,
      })),
    },
  });

  if (canFireFbq()) {
    (window as any).fbq('track', 'InitiateCheckout', {
      value: total,
      currency: 'EUR',
      content_ids: items.map(i => i.id),
      content_type: 'product',
      contents: items.map(i => ({ id: i.id, quantity: i.quantity, item_price: i.price })),
      num_items: items.reduce((sum, i) => sum + i.quantity, 0),
    });
  }
}

/** Stripe EmbeddedCheckout rendered on screen. */
export function trackCheckoutLoaded(): void {
  push('checkout_loaded', {});
}

export function trackPurchase(
  orderId: string,
  total: number,
  items: Array<{ id: string; name: string; price: number; quantity: number }>,
  coupon?: string,
): void {
  push('purchase', {
    ecommerce: {
      transaction_id: orderId,
      currency: 'EUR',
      value: total,
      ...(coupon ? { coupon } : {}),
      items: items.map((i) => ({
        item_id: i.id,
        item_name: i.name,
        price: i.price,
        quantity: i.quantity,
      })),
    },
  });

  if (canFireFbq()) {
    // eventID = order number: Meta deduplicates identical event_name+eventID
    // pairs, so a thank-you page reload can't count the purchase twice.
    (window as any).fbq('track', 'Purchase', {
      value: total,
      currency: 'EUR',
      content_ids: items.map(i => i.id),
      content_type: 'product',
      contents: items.map(i => ({ id: i.id, quantity: i.quantity, item_price: i.price })),
      num_items: items.reduce((sum, i) => sum + i.quantity, 0),
    }, { eventID: orderId });
  }
}

export function trackPurchaseError(error: string): void {
  push('purchase_error', { error });
}

/* ─── Waitlist / lead-generation events ──────────────────────────────── */

export function trackWaitlistStart(offerType: string): void {
  push('waitlist_start', { offer_type: offerType });
}

export function trackWaitlistComplete(offerType: string, marketingOptIn: boolean): void {
  push('waitlist_complete', {
    offer_type: offerType,
    marketing_opt_in: marketingOptIn,
  });
}

export function trackMarketingOptIn(source: string): void {
  push('marketing_opt_in', { source });
}
/* ─── Product lists (GA4 view_item_list / select_item) ──────────────── */

type ListItem = { item_id: string; item_name: string; price: number; index: number };

export function trackViewItemList(listId: string, items: ListItem[]): void {
  push('view_item_list', { ecommerce: { item_list_id: listId, item_list_name: listId, items } });
}

export function trackSelectItem(listId: string, item: ListItem): void {
  push('select_item', { ecommerce: { item_list_id: listId, item_list_name: listId, items: [item] } });
}

/** Generic CTA click (e.g. hero buttons). */
export function trackCtaClick(label: string): void {
  push('cta_click', { cta_label: label });
}

/** GA4 view_item + Meta ViewContent with the actual displayed price. */
export function trackViewItem(productId: string, productName: string, price: number, quantity = 1): void {
  push('view_item', {
    ecommerce: {
      currency: 'EUR',
      value: price * quantity,
      items: [{ item_id: productId, item_name: productName, price, quantity }],
    },
  });
  if (canFireFbq()) {
    (window as any).fbq('track', 'ViewContent', {
      value: price * quantity,
      currency: 'EUR',
      content_ids: [productId],
      content_name: productName,
      content_type: 'product',
      contents: [{ id: productId, quantity, item_price: price }],
    });
  }
}
